/**
 * "Necesita tu atención" — lo primero que ve People al entrar al panel.
 *
 * Traduce el estado del flow a acciones concretas: qué pasó, por qué, y qué
 * hacer. Nada de códigos técnicos: los nombres de pasos y los errores salen de
 * `plain-language.ts`. Si no hay nada pendiente, lo dice y listo.
 */

import { isInAlertWindow } from '@/../../shared/people-flow-window';
import { currentLocale } from '@/lib/locale';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, ClipboardList, ChevronRight, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import type { PeopleFlowAltaStatus } from '@/../../shared/types/people-flow';
import { explainError, stepTitle } from '@/components/users/onboarding-flow/plain-language';
import { useTranslation } from 'react-i18next';

type Item = {
  key: string;
  idAlta: string;
  nombre: string;
  ingreso: string | null;
  kind: 'missing_data' | 'failed_step';
  stepId?: string;
  what: string;
  todo: string;
  technical?: string;
};

function fmtIngreso(iso: string | null): string {
  if (!iso) return 'sin fecha';
  const d = new Date(iso);
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  const fecha = d.toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short' });
  if (days === 0) return `entra hoy (${fecha})`;
  if (days === 1) return `entra mañana (${fecha})`;
  if (days > 1) return `entra en ${days} días (${fecha})`;
  if (days === -1) return `entró ayer (${fecha})`;
  return `entró hace ${Math.abs(days)} días (${fecha})`;
}

export function NeedsAttention({ rows }: { rows: PeopleFlowAltaStatus[] }) {
  const { t } = useTranslation();
  const [showTech, setShowTech] = useState<Record<string, boolean>>({});
  const utils = trpc.useUtils();
  const runStep = trpc.peopleFlow.runStepNow.useMutation({
    onSuccess: () => {
      toast.success(t('admin.needsattention.listo_lo_reintentamos'));
      utils.peopleFlow.getFlowStatus.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    for (const row of rows) {
      // Solo pedimos acción dentro de la ventana de alerta (KP 21/09): próxima
      // semana hábil o última semana. Misma regla que el cajón Errors.
      const relevante = isInAlertWindow(row.alta.fechaIngresoParsed);
      if (!relevante) continue;

      if (!row.validation.complete && row.validation.missingLabels.length > 0) {
        out.push({
          key: `${row.alta.idAlta}-datos`,
          idAlta: row.alta.idAlta,
          nombre: row.alta.nombre,
          ingreso: row.alta.fechaIngresoParsed,
          kind: 'missing_data',
          what: t('admin.needsattention.faltan_datos_en_la_planilla', { fields: row.validation.missingLabels.join(', ') }),
          todo: t('admin.needsattention.mientras_falten_el_resto_del_onboarding'),
        });
      }

      for (const s of row.steps) {
        if (s.status !== 'error' || String(s.stepId) === 'validation') continue;
        const e = explainError(t, s.error ?? s.raw, String(s.stepId));
        out.push({
          key: `${row.alta.idAlta}-${s.stepId}`,
          idAlta: row.alta.idAlta,
          nombre: row.alta.nombre,
          ingreso: row.alta.fechaIngresoParsed,
          kind: 'failed_step',
          stepId: String(s.stepId),
          what: e.what,
          todo: e.todo,
          technical: e.technical,
        });
      }
    }
    // Los ingresos más próximos primero
    return out.sort((a, b) => (a.ingreso ?? '').localeCompare(b.ingreso ?? ''));
  }, [rows, t]);

  if (items.length === 0) {
    return (
      <Card className="flex items-center gap-3 p-4">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div>
          <p className="text-sm font-medium">{t('admin.needsattention.todo_en_orden')}</p>
          <p className="text-xs text-muted-foreground">
            {t('admin.needsattention.no_hay_ingresos_esperando_datos_ni')}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <h3 className="text-sm font-semibold">{t('admin.needsattention.necesita_tu_atencion')}</h3>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
          {items.length}
        </span>
      </div>

      <div className="space-y-2.5">
        {items.map((item) => (
          <div key={item.key} className="rounded-lg border p-3">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-sm font-medium">{item.nombre}</span>
              <span className="text-xs text-muted-foreground">{fmtIngreso(item.ingreso)}</span>
              {item.kind === 'failed_step' && (
                <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-3 w-3" />
                  {stepTitle(t, item.stepId)}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm">{item.what}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{item.todo}</p>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Link
                to={`/admin/users/people-panel?tab=errores&alta=${item.idAlta}`}
                className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline"
              >
                {t('admin.needsattention.ver_el_proceso_completo')}
                <ChevronRight className="h-3 w-3" />
              </Link>
              {item.kind === 'failed_step' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs"
                  disabled={runStep.isPending}
                  onClick={() =>
                    runStep.mutate({ idAlta: item.idAlta, stepId: item.stepId!, mode: 'retry' })
                  }
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('admin.needsattention.reintentar_ahora')}
                </Button>
              )}
              {item.technical && (
                <button
                  className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => setShowTech((p) => ({ ...p, [item.key]: !p[item.key] }))}
                >
                  {showTech[item.key] ? t('admin.needsattention.ocultar_detalle_tecnico') : t('admin.needsattention.ver_detalle_tecnico')}
                </button>
              )}
            </div>
            {showTech[item.key] && item.technical && (
              <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-[11px] text-muted-foreground">
                {item.technical}
              </pre>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
